import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Icon from 'assets/components/icon/Icon';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Contract from 'models/sales/contract/Model.Contract';
import useClaimCreator from 'module/claims/hooks/useClaimCreator';
import { useState } from 'react';
import ClaimSimpleClaimForm from '../simpleClaim/Claim.SimpleClaimForm';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import { useHistory } from 'react-router';
import { claimCreateIndexRoute } from 'app/pages/claims/createClaim/Index';
import { ClaimCreateNavigationState } from 'module/claims/helper/Claim.CreateNavigation';
import {
  canCreateClaimForContractStatus,
  isClaimContractExpired,
  isClaimContractExpiredWindowExceeded,
} from 'module/claims/helper/Claim.ContractEligibility';

enum ClaimCreationType {
  quick = 'quick',
  simple = 'simple',
}
const ClaimCreateButtons = ({
  contract,
  claimCreatorHook,
  onSaved,
}: {
  contract: Contract;
  claimCreatorHook: ReturnType<typeof useClaimCreator>;
  onSaved?: () => void;
}) => {
  const { lang } = getLocales();
  const history = useHistory<ClaimCreateNavigationState>();
  const { isDealer, dealerId } = useAuthStore();
  const { canCreateClaim, canCreateSimpleClaim, canCreateClaimForExpiredContract } = claimCreatorHook;

  const expiredContract = isClaimContractExpired(contract);
  const isExpiredWindowExceeded = isClaimContractExpiredWindowExceeded(contract);
  const hasAllowedStatus = canCreateClaimForContractStatus(contract);

  const [simpleClaimData, setSimpleClaimData] = useState<Contract>();

  const handleSaved = () => {
    setSimpleClaimData(undefined);
    onSaved?.();
  };

  const [showExpiredConfirm, setShowExpiredConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState<keyof typeof ClaimCreationType>(undefined);

  const handleExpiredCheck = (action: keyof typeof ClaimCreationType) => {
    if (isDisabled(action)) return;
    if (expiredContract) {
      setPendingAction(action);
      setShowExpiredConfirm(true);
      return;
    }
    if (action === ClaimCreationType.quick) openCreateClaim();
    if (action === ClaimCreationType.simple) setSimpleClaimData(contract);
  };

  const proceedAfterConfirm = () => {
    if (pendingAction === ClaimCreationType.quick) {
      openCreateClaim();
    }
    if (pendingAction === ClaimCreationType.simple) {
      setSimpleClaimData(contract);
    }
    setShowExpiredConfirm(false);
    setPendingAction(null);
  };

  const isDisabled = (type: keyof typeof ClaimCreationType) => {
    if (isExpiredWindowExceeded) return true;

    const canCreateClaimInternal = hasAllowedStatus && canCreateClaim;
    const canCreateSimpleClaimInternal = hasAllowedStatus && canCreateSimpleClaim;

    if (expiredContract && type === ClaimCreationType.quick)
      return !(canCreateSimpleClaimInternal && canCreateClaimForExpiredContract);
    if (expiredContract && type === ClaimCreationType.simple)
      return !(canCreateSimpleClaimInternal && canCreateClaimForExpiredContract);
    if (type === ClaimCreationType.quick) return !canCreateClaimInternal;
    if (type === ClaimCreationType.simple) return !canCreateSimpleClaimInternal;

    return true;
  };

  const openCreateClaim = () => {
    if (isDisabled(ClaimCreationType.quick)) return;
    history.push(claimCreateIndexRoute, {
      createForContract: {
        contract: contract.toJSON(),
        claimCreation: {
          claimCreationOption: isDealer ? ClaimCreatorOptions.DEALERSHIP : undefined,
          dealerId: isDealer ? dealerId : undefined,
        },
        isForExpired: expiredContract,
      },
    });
  };

  return (
    <>
      {/* EXPIRED CONFIRM MODAL */}
      {showExpiredConfirm && (
        <Confirm
          onConfirm={proceedAfterConfirm}
          onClose={() => {
            setShowExpiredConfirm(false);
            setPendingAction(null);
          }}
          actionName={lang.expiredClaimCreationWarning}
          itemName={contract?.displayInfo?.title}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}

      <Button
        class={ThemeButtonCircle}
        media="fas-car-crash"
        onClick={() => handleExpiredCheck(ClaimCreationType.quick)}
        disabled={isDisabled(ClaimCreationType.quick)}
        active
        htmlElementProps={{
          title: isExpiredWindowExceeded
            ? lang.expiredContractClaimWindowExceeded
            : expiredContract
              ? lang.createClaimForExpiredContract
              : lang.createClaimForContract,
        }}
      />
      {
        //#region SIMPLE CLAIM
        <>
          <Button
            media={<Icon class="fas-car-side" />}
            class={ThemeButtonCircle}
            htmlElementProps={{
              title: isExpiredWindowExceeded
                ? lang.expiredContractClaimWindowExceeded
                : expiredContract
                  ? lang.createClaimForExpiredContract
                  : lang.createSimpleClaim,
            }}
            disabled={isDisabled(ClaimCreationType.simple)}
            active
            onClick={() => handleExpiredCheck(ClaimCreationType.simple)}
          />
          {simpleClaimData && (
            <ClaimSimpleClaimForm
              contract={simpleClaimData}
              onClose={() => setSimpleClaimData(undefined)}
              onSaved={handleSaved}
            />
          )}
        </>
        //#endregion
      }
    </>
  );
};

export default ClaimCreateButtons;
